const API_BASE = ''

class ApiService {
    getToken() {
        const user = localStorage.getItem('rdi_user')
        if (user) {
            try {
                const parsed = JSON.parse(user)
                return parsed.token
            } catch {
                return null
            }
        }
        return null
    }

    async request(method, endpoint, data = null, options = {}) {
        const url = `${API_BASE}${endpoint}`
        const token = this.getToken()

        const config = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            credentials: 'include',
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
            config.body = JSON.stringify(data)
        }

        try {
            const response = await fetch(url, config)

            const contentType = response.headers.get('content-type')
            if (contentType && contentType.includes('application/json')) {
                const json = await response.json()
                return json
            } else {
                const text = await response.text()
                return { success: response.ok, data: text }
            }
        } catch (error) {
            console.error('API request failed:', error)
            throw error
        }
    }

    get(endpoint, options = {}) {
        return this.request('GET', endpoint, null, options)
    }

    post(endpoint, data, options = {}) {
        return this.request('POST', endpoint, data, options)
    }

    put(endpoint, data, options = {}) {
        return this.request('PUT', endpoint, data, options)
    }

    patch(endpoint, data, options = {}) {
        return this.request('PATCH', endpoint, data, options)
    }

    delete(endpoint, options = {}) {
        return this.request('DELETE', endpoint, null, options)
    }
}

export const api = new ApiService()

// Timesheet API
export const timesheetApi = {
    getAll: (filters = {}) => {
        const params = new URLSearchParams(filters).toString()
        return api.get(`/api/timesheets${params ? `?${params}` : ''}`)
    },
    getById: (uuid) => api.get(`/api/timesheets/${uuid}`),
    create: (data) => api.post('/api/timesheets', data),
    update: (uuid, data) => api.put(`/api/timesheets/${uuid}`, data),
    delete: (uuid) => api.delete(`/api/timesheets/${uuid}`),
    submit: (uuid) => api.post(`/api/timesheets/${uuid}/submit`),
    returnToDraft: (uuid) => api.post(`/api/timesheets/${uuid}/return-to-draft`),
    saveSignature: (uuid, signatureData) => api.post(`/api/timesheets/${uuid}/signature`, { signature: signatureData }),
    saveSketch: (uuid, sketchData) => api.post(`/api/timesheets/${uuid}/sketch`, { sketch: sketchData }),

    // Packing Slip Items
    getPackingSlipItems: (uuid) => api.get(`/api/timesheets/${uuid}/packing-slip-items`),
    addPackingSlipItem: (uuid, data) => api.post(`/api/timesheets/${uuid}/packing-slip-items`, data),
    updatePackingSlipItem: (itemUuid, data) => api.put(`/api/timesheets/packing-slip-items/${itemUuid}`, data),
    deletePackingSlipItem: (itemUuid) => api.delete(`/api/timesheets/packing-slip-items/${itemUuid}`),

    // Pickup Slip Items
    getPickupSlipItems: (uuid) => api.get(`/api/timesheets/${uuid}/pickup-slip-items`),
    addPickupSlipItem: (uuid, data) => api.post(`/api/timesheets/${uuid}/pickup-slip-items`, data),
    updatePickupSlipItem: (itemUuid, data) => api.put(`/api/timesheets/pickup-slip-items/${itemUuid}`, data),
    deletePickupSlipItem: (itemUuid) => api.delete(`/api/timesheets/pickup-slip-items/${itemUuid}`),

    // Vendor Slip Items
    getVendorSlipItems: (uuid) => api.get(`/api/timesheets/${uuid}/vendor-slip-items`),
    addVendorSlipItem: (uuid, data) => api.post(`/api/timesheets/${uuid}/vendor-slip-items`, data),
    updateVendorSlipItem: (itemUuid, data) => api.put(`/api/timesheets/vendor-slip-items/${itemUuid}`, data),
    deleteVendorSlipItem: (itemUuid) => api.delete(`/api/timesheets/vendor-slip-items/${itemUuid}`),

    // Parts Used
    getParts: (uuid) => api.get(`/api/timesheets/${uuid}/parts`),
    addPart: (uuid, data) => api.post(`/api/timesheets/${uuid}/parts`, data),
    updatePart: (partUuid, data) => api.put(`/api/timesheets/parts/${partUuid}`, data),
    deletePart: (partUuid) => api.delete(`/api/timesheets/parts/${partUuid}`),
    searchParts: (query) => api.get(`/api/timesheets/parts/search?q=${encodeURIComponent(query)}`),

    // Drawings / Sketches
    getDrawings: (referenceNo) => api.get(`/api/timesheets/drawings?referenceNo=${encodeURIComponent(referenceNo)}`),
    getDrawing: (drawingId) => api.get(`/api/timesheets/drawings/${drawingId}`),
    saveDrawing: (data) => api.post('/api/timesheets/drawings', data),
    deleteDrawing: (drawingId) => api.delete(`/api/timesheets/drawings/${drawingId}`),
}

// Job API
export const jobApi = {
    getAll: () => api.get('/api/jobs'),
    getByWorkOrder: (workOrder) => api.get(`/api/jobs/${workOrder}`),
}

// Customer API
export const customerApi = {
    getAll: () => api.get('/api/customers'),
    getById: (id) => api.get(`/api/customers/${id}`),
    create: (data) => api.post('/api/customers', data),
    update: (id, data) => api.put(`/api/customers/${id}`, data),
    delete: (id) => api.delete(`/api/customers/${id}`),
}

// User API
export const userApi = {
    getAll: () => api.get('/api/users'),
    getById: (id) => api.get(`/api/users/${id}`),
    create: (data) => api.post('/api/users', data),
    update: (id, data) => api.put(`/api/users/${id}`, data),
    delete: (id) => api.delete(`/api/users/${id}`),
}

// Inventory API
export const inventoryApi = {
    getAll: () => api.get('/api/inventory'),
    getById: (id) => api.get(`/api/inventory/${id}`),
    search: (query) => api.get(`/api/inventory/search?q=${encodeURIComponent(query)}`),
    create: (data) => api.post('/api/inventory', data),
    update: (id, data) => api.put(`/api/inventory/${id}`, data),
    updateQuantity: (id, quantity) => api.patch(`/api/inventory/${id}/quantity`, { quantity }),
    delete: (id) => api.delete(`/api/inventory/${id}`),
}

// Sync API
export const syncApi = {
    getStatus: () => api.get('/api/sync/status'),
    trigger: (commandType, payload = null) => api.post('/api/sync/trigger', { commandType, payload }),
    getPendingTimesheets: () => api.get('/api/sync/pending-timesheets'),
    getJobTimesheets: (workOrderNumber) => api.get(`/api/sync/job-timesheets?workOrderNumber=${encodeURIComponent(workOrderNumber)}`),
    getExportedTimesheets: (jobNumber) => api.get(`/api/sync/exported-timesheets?jobNumber=${encodeURIComponent(jobNumber)}`),
    rollbackTimesheets: (workOrderNumber, uuids, referenceNumbers) => api.post('/api/sync/rollback', { WorkOrderNumber: workOrderNumber, Uuids: uuids, ReferenceNumbers: referenceNumbers }),
}

// Company Settings API
export const companyApi = {
    getSettings: () => api.get('/api/company-settings'),
    updateSettings: (data) => api.put('/api/company-settings', data),
    uploadLogo: (side, file) => {
        const formData = new FormData()
        formData.append('file', file)
        const token = api.getToken()
        return fetch(`/api/company-settings/logo/${side}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        }).then(res => res.json())
    },
}